import { z } from "zod";
import { FocusSchema, LocaleSchema, TimestampSchema } from "./common.js";
import { IdSchema } from "./ids.js";
import { EffortLevelSchema, ModelPreferenceSchema } from "./model-policy.js";
import { ApprovalRequestSchema, ApprovalStatusSchema } from "./tool.js";
/** Task lifecycle (DESIGN §4). Cancellation happens only by explicit human action. */
export const TASK_STATES = [
    "received",
    "routing",
    "planning",
    "in_progress",
    "review",
    "awaiting_approval",
    "done",
    "needs_owner",
    "cancelled",
];
export const TaskStateSchema = z.enum(TASK_STATES);
/** Points at a file produced by a task, inside the project's artifacts dir. */
export const ArtifactRefSchema = z.object({
    projectId: IdSchema,
    /** Path relative to the project's `artifacts/` directory. */
    path: z.string().min(1),
    mediaType: z.string().min(1),
    url: z.string().optional(),
});
export const SubmitTaskRequestSchema = z.object({
    companyId: IdSchema,
    requestedBy: IdSchema,
    text: z.string().min(1),
    focus: FocusSchema.nullable(),
    locale: LocaleSchema,
    /** Client-chosen id for idempotent retries. */
    clientRequestId: z.string().min(1),
    /** Set when the task was spawned by an accepted inter-company contract. */
    contractId: IdSchema.optional(),
    /** Per-message model/effort override (policy may ignore it). */
    modelPreference: ModelPreferenceSchema.optional(),
});
export const TaskSchema = z.object({
    id: IdSchema,
    companyId: IdSchema,
    requestedBy: IdSchema,
    text: z.string(),
    locale: LocaleSchema,
    state: TaskStateSchema,
    floorId: IdSchema.nullable(),
    /** Final summary in the user's locale, when done. */
    summary: z.string().nullable(),
    artifacts: z.array(ArtifactRefSchema),
    /** Why the task needs the owner. */
    diagnosis: z.string().nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
/** One item of a task's update stream. */
export const TaskUpdateSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("state"), state: TaskStateSchema, detail: z.string().optional() }),
    z.object({ kind: z.literal("message_delta"), messageId: z.string(), delta: z.string() }),
    z.object({ kind: z.literal("message"), messageId: z.string(), text: z.string() }),
    z.object({ kind: z.literal("approval_request"), approval: ApprovalRequestSchema }),
    z.object({
        kind: z.literal("approval_resolved"),
        approvalId: IdSchema,
        status: ApprovalStatusSchema,
    }),
    z.object({ kind: z.literal("artifact"), artifact: ArtifactRefSchema }),
    /** The model and effort a run of this task was resolved to (shown on the Steps card). */
    z.object({
        kind: z.literal("model"),
        model: z.string().min(1),
        effort: EffortLevelSchema.optional(),
        /** Which layer decided it: message, assistant, company or agent. */
        source: z.string().optional(),
    }),
]);
export const TaskStreamItemSchema = z.object({
    /** Strictly increasing per task, starting at 1. Used to resume without loss or duplication. */
    seq: z.number().int().positive(),
    taskId: IdSchema,
    at: TimestampSchema,
    update: TaskUpdateSchema,
});
/** HTTP routes of the company node API (served by the company-node service). */
export const NODE_API = {
    tasks: "/v1/tasks",
    task: "/v1/tasks/:taskId",
    /** WebSocket; query `afterSeq`. */
    taskStream: "/v1/tasks/:taskId/stream",
    approvalDecision: "/v1/approvals/:approvalId/decision",
    /** WebSocket; query `afterSeq`. */
    entityChanges: "/v1/entities/changes",
    events: "/v1/events",
    interCompanyInbox: "/v1/intercompany/inbox",
};
//# sourceMappingURL=node.js.map